/**
 * Hook for managing job execution history, cancel/retry actions,
 * and computed job lists (in-progress, completed, filtered by resource type).
 */
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { JobRun, JobStage, createInitialStages, AgentLogEntry, LambdaFunction, SelectedVersions, Account } from '../types';
import { PropertyFilterProps } from '@amzn/awsui-components-core/property-filter';
import { UpgradeMedicDataService } from 'src/services/upgrade-medic/UpgradeMedicDataService';
import { RUNTIME_CONFIGS, getLambdaCountByLanguage, WEBSOCKET_URL } from '../config';

const MAX_CONCURRENT_JOBS = 3;

export const useJobTracking = (
  userAlias: string,
  accounts: Account[],
  allLambdas: LambdaFunction[],
  currentConfigName: string,
  addLog: (level: AgentLogEntry['level'], message: string, stage?: string, pipelineId?: string) => void,
  wsRef: React.MutableRefObject<WebSocket | null>,
  wsMapRef: React.MutableRefObject<Map<string, WebSocket>>
) => {
  const [jobHistory, setJobHistory] = useState<JobRun[]>([]);
  const [selectedJobForView, setSelectedJobForView] = useState<JobRun | null>(null);
  const [jobExecutionFilter, setJobExecutionFilter] = useState<'in-progress' | 'completed'>('in-progress');

  // PropertyFilter state
  const [jobFilterQuery, setJobFilterQuery] = useState<PropertyFilterProps.Query>({
    tokens: [],
    operation: 'and'
  });

  // Current job tracking
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const currentJobIdRef = useRef<string | null>(null);
  const isJobRunningRef = useRef(false);

  // Cleanup: close all WebSocket connections on unmount to prevent connection leaks
  useEffect(() => {
    return () => {
      wsRef.current?.close();
      wsMapRef.current.forEach((ws) => ws.close());
      wsMapRef.current.clear();
    };
  }, []);

  // Fetch job history on mount
  useEffect(() => {
    const fetchJobHistory = async () => {
      try {
        console.log('[Upgrade Medic] Fetching job history from DynamoDB...');
        const jobs = await UpgradeMedicDataService.getJobRunHistory('Lambda');
        console.log(`[Upgrade Medic] Loaded ${jobs.length} jobs from DynamoDB`);

        // Items are already normalized by getJobRunHistory — map directly to JobRun
        const jobRuns: JobRun[] = jobs.map((item: any) => ({
          jobId: item.jobId,
          pipelineId: item.pipelineId,
          status: item.jobStatus || item.status || 'failed',
          triggeredBy: item.triggeredBy,
          startedAt: new Date(item.startedAt),
          completedAt: item.completedAt ? new Date(item.completedAt) : undefined,
          lastUpdated: item.lastUpdated ? new Date(item.lastUpdated) : undefined,
          pipelines: item.pipelines,
          functions: item.functions,
          targetVersions: item.targetVersions,
          resourceType: item.resourceType,
          progress: item.progress,
          progressMessage: item.progressMessage,
          currentStage: (item.currentStage as JobStage) || undefined,
          stages: item.stages || createInitialStages(),
          artifacts: item.artifacts,
          error: item.error,
          totalResources: item.totalResources,
          completedResources: item.completedResources,
          failedResources: item.failedResources,
          logs: [],
          streamingText: '',
          result: item.result
        }));

        setJobHistory(jobRuns);
      } catch (error) {
        console.error('[Upgrade Medic] Failed to load job history from DynamoDB:', error);
      }
    };
    fetchJobHistory();
  }, []);

  // Per-user running jobs
  const userRunningJobs = useMemo(
    () => jobHistory.filter((j) => (j.status === 'running' || j.status === 'connecting') && j.triggeredBy === userAlias),
    [jobHistory, userAlias]
  );
  const runningJobCount = userRunningJobs.length;
  const canStartNewJob = runningJobCount < MAX_CONCURRENT_JOBS;

  // Resources currently being updated (conflict detection)
  const resourcesInProgress = useMemo(() => {
    const resources = new Set<string>();
    userRunningJobs.forEach((job) => {
      job.pipelines.forEach((p) => resources.add(p));
    });
    return resources;
  }, [userRunningJobs]);

  // Filtered by resource type
  const resourceTypeJobs = useMemo(() => jobHistory.filter((j) => j.resourceType === currentConfigName), [jobHistory, currentConfigName]);
  const resourceInProgressJobs = useMemo(
    () =>
      resourceTypeJobs
        .filter((j) => j.status === 'running' || j.status === 'connecting')
        .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()),
    [resourceTypeJobs]
  );
  const resourceCompletedJobs = useMemo(
    () =>
      resourceTypeJobs
        .filter(
          (j) =>
            j.status === 'completed' || j.status === 'completed_with_alpha_deployment_failure' || j.status === 'failed' || j.status === 'cancelled'
        )
        .sort((a, b) => {
          const aTime = new Date(a.completedAt || a.startedAt).getTime();
          const bTime = new Date(b.completedAt || b.startedAt).getTime();
          return bTime - aTime;
        }),
    [resourceTypeJobs]
  );

  // Global filtered lists
  const inProgressJobs = useMemo(() => jobHistory.filter((j) => j.status === 'running' || j.status === 'connecting'), [jobHistory]);
  const completedJobs = useMemo(
    () =>
      jobHistory.filter(
        (j) => j.status === 'completed' || j.status === 'completed_with_alpha_deployment_failure' || j.status === 'failed' || j.status === 'cancelled'
      ),
    [jobHistory]
  );

  // Cancel job
  const handleCancelJob = useCallback(
    async (job: JobRun) => {
      if (job.triggeredBy !== userAlias) {
        addLog('error', '❌ You can only cancel jobs that you started');
        return;
      }
      addLog('warning', `⚠️ Cancelling job ${job.jobId}...`);
      try {
        await UpgradeMedicDataService.cancelJobRun(job.jobId, userAlias, job.currentStage);

        const cancelPayload = JSON.stringify({
          type: 'cancel_job',
          jobId: job.jobId,
          userId: userAlias
        });

        // Try to find an open WS for this job in the map, then fall back to wsRef, then temp connection
        const jobWs = wsMapRef.current.get(job.jobId);
        const activeWs =
          jobWs && jobWs.readyState === WebSocket.OPEN ? jobWs : wsRef.current && wsRef.current.readyState === WebSocket.OPEN ? wsRef.current : null;

        if (activeWs) {
          activeWs.send(cancelPayload);
          addLog('tool', `📤 Sent cancel request to server for job ${job.jobId}`);
        } else {
          // Open a temporary connection — wait for 'connected' handshake before sending
          const tempWs = new WebSocket(WEBSOCKET_URL);
          const tempTimeout = setTimeout(() => {
            addLog('warning', `⚠️ Cancel connection timeout for job ${job.jobId} — cancelled locally only`);
            tempWs.close();
          }, 5000);
          tempWs.onmessage = (event) => {
            try {
              const msg = JSON.parse(event.data);
              if (msg.type === 'connected') {
                tempWs.send(cancelPayload);
                addLog('tool', `📤 Sent cancel request to server for job ${job.jobId} (via new connection)`);
                clearTimeout(tempTimeout);
                tempWs.close();
              }
            } catch {
              // ignore non-JSON messages
            }
          };
          tempWs.onerror = () => {
            clearTimeout(tempTimeout);
            addLog('warning', `⚠️ Could not reach server to cancel job ${job.jobId} — cancelled locally only`);
          };
        }

        setJobHistory((prev) =>
          prev.map((j) =>
            j.jobId === job.jobId ? { ...j, status: 'cancelled' as const, completedAt: new Date(), progressMessage: `Cancelled by ${userAlias}` } : j
          )
        );
        if (currentJobIdRef.current === job.jobId) {
          isJobRunningRef.current = false;
        }
        addLog('phase', `✅ Job ${job.jobId} cancelled successfully`);
      } catch (error) {
        console.error('[Upgrade Medic] Failed to cancel job:', error);
        addLog('error', `❌ Failed to cancel job: ${error}`);
      }
    },
    [userAlias, addLog, wsRef, wsMapRef]
  );

  // Retry failed job
  const handleRetryJob = useCallback(
    (job: JobRun): { lambdaIdsToSelect: Set<string>; versions: SelectedVersions } => {
      const lambdaIdsToSelect = new Set<string>();
      accounts.forEach((account) => {
        account.pipelines.forEach((pipeline) => {
          if (job.pipelines.includes(pipeline.name)) {
            pipeline.lambdas.forEach((lambda) => {
              if (job.functions.includes(lambda.name) || job.functions.length === 0) {
                lambdaIdsToSelect.add(lambda.id);
              }
            });
          }
        });
      });

      let versions: SelectedVersions;
      if (job.targetVersions && Object.keys(job.targetVersions).length > 0) {
        versions = job.targetVersions;
      } else {
        const selectedLambdasForRetry = allLambdas.filter((l) => lambdaIdsToSelect.has(l.id));
        const languageCounts = getLambdaCountByLanguage(selectedLambdasForRetry);
        versions = {};
        Object.keys(languageCounts).forEach((lang) => {
          const config = RUNTIME_CONFIGS.find((c) => c.language === lang);
          if (config) {
            const recommended = config.versions.find((v) => v.isRecommended);
            versions[lang] = recommended?.value || config.versions[0]?.value || '';
          }
        });
      }

      return { lambdaIdsToSelect, versions };
    },
    [accounts, allLambdas]
  );

  // PropertyFilter helpers
  const matchesOperator = (fieldValue: string, searchValue: string, operator?: string): boolean => {
    switch (operator) {
      case '=':
        return fieldValue === searchValue;
      case '!=':
        return fieldValue !== searchValue;
      case ':':
        return fieldValue.includes(searchValue);
      case '!:':
        return !fieldValue.includes(searchValue);
      default:
        return fieldValue.includes(searchValue);
    }
  };

  const filterJobsByQuery = useCallback((jobs: JobRun[], query: PropertyFilterProps.Query): JobRun[] => {
    if (!query.tokens || query.tokens.length === 0) return jobs;
    return jobs.filter((job) => {
      const results = query.tokens.map((token) => {
        const value = String(token.value || '').toLowerCase();
        switch (token.propertyKey) {
          case 'jobId':
            return matchesOperator(job.jobId.toLowerCase(), value, token.operator);
          case 'status':
            return matchesOperator(job.status.toLowerCase(), value, token.operator);
          case 'triggeredBy':
            return matchesOperator(job.triggeredBy.toLowerCase(), value, token.operator);
          case 'pipeline':
            return job.pipelines.some((p) => matchesOperator(p.toLowerCase(), value, token.operator));
          case 'stage':
            return matchesOperator((job.currentStage || '').toLowerCase(), value, token.operator);
          default:
            return (
              job.jobId.toLowerCase().includes(value) ||
              job.status.toLowerCase().includes(value) ||
              job.triggeredBy.toLowerCase().includes(value) ||
              job.pipelines.some((p) => p.toLowerCase().includes(value)) ||
              (job.currentStage || '').toLowerCase().includes(value)
            );
        }
      });
      return query.operation === 'and' ? results.every((r) => r) : results.some((r) => r);
    });
  }, []);

  return {
    jobHistory,
    setJobHistory,
    selectedJobForView,
    setSelectedJobForView,
    jobExecutionFilter,
    setJobExecutionFilter,
    jobFilterQuery,
    setJobFilterQuery,
    currentJobId,
    setCurrentJobId,
    currentJobIdRef,
    isJobRunningRef,
    // Computed
    userRunningJobs,
    runningJobCount,
    canStartNewJob,
    resourcesInProgress,
    resourceTypeJobs,
    resourceInProgressJobs,
    resourceCompletedJobs,
    inProgressJobs,
    completedJobs,
    // Actions
    handleCancelJob,
    handleRetryJob,
    filterJobsByQuery,
    MAX_CONCURRENT_JOBS
  };
};
